import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { LeftoverService } from "../../services/leftover.service";
import * as LeftoverActions from './leftover.actions';
import { catchError, debounceTime, distinctUntilChanged, filter, map, mergeMap, of, switchMap, takeUntil } from "rxjs";
import { NotificationService } from "../../services/notification.service";
import { TranslateService } from "@ngx-translate/core";

export const loadLeftovers$ = createEffect(
  (
    actions$ = inject(Actions),
    leftoverService = inject(LeftoverService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(LeftoverActions.loadLeftovers),
      mergeMap(() =>
        leftoverService.getLeftovers().pipe(
          map((leftovers) => {
            // Optional:
            // notification.success('Leftovers loaded.');
            return LeftoverActions.loadLeftoversSuccess({ leftovers });
          }),
          catchError((error) => {
            notification.error(translate.instant('leftover.notifications.loadFailure'));
            return of(LeftoverActions.loadLeftoversFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const addLeftover$ = createEffect(
  (
    actions$ = inject(Actions),
    leftoverService = inject(LeftoverService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(LeftoverActions.addLeftover),
      mergeMap(({ leftover }) =>
        leftoverService.addLeftover(leftover).pipe(
          map((created) => {
            notification.success(translate.instant('leftover.notifications.addSuccess'));
            return LeftoverActions.addLeftoverSuccess({ leftover: created });
          }),
          catchError((error) => {
            notification.error(translate.instant('leftover.notifications.addFailure'));
            return of(LeftoverActions.addLeftoverFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateLeftover$ = createEffect(
  (
    actions$ = inject(Actions),
    leftoverService = inject(LeftoverService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(LeftoverActions.updateLeftover),
      mergeMap(({ leftover }) =>
        leftoverService.updateLeftover(leftover).pipe(
          map((updated) => {
            notification.success(translate.instant('leftover.notifications.updateSuccess'));
            return LeftoverActions.updateLeftoverSuccess({ leftover: updated });
          }),
          catchError((error) => {
            notification.error(translate.instant('leftover.notifications.updateFailure'));
            return of(LeftoverActions.updateLeftoverFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteLeftover$ = createEffect(
  (
    actions$ = inject(Actions),
    leftoverService = inject(LeftoverService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(LeftoverActions.deleteLeftover),
      mergeMap(({ leftoverId }) =>
        leftoverService.deleteLeftover(leftoverId).pipe(
          map(() => {
            notification.success(translate.instant('leftover.notifications.deleteSuccess'));
            return LeftoverActions.deleteLeftoverSuccess({ leftoverId });
          }),
          catchError((error) => {
            notification.error(translate.instant('leftover.notifications.deleteFailure'));
            return of(LeftoverActions.deleteLeftoverFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const searchLeftoversBrief$ = createEffect(
  (
    actions$ = inject(Actions),
    leftoverService = inject(LeftoverService),
  ) =>
    actions$.pipe(
      ofType(LeftoverActions.updateLeftoverSearchTerm, LeftoverActions.searchLeftoversBrief),
      map((a) => 'q' in a ? a.q : a.term),
      map((q) => (q ?? '').trim()),
      debounceTime(250),
      distinctUntilChanged(),
      filter((q) => q.length >= 2),
      switchMap((q) =>
        leftoverService.searchLeftoversBrief(q, 20).pipe(
          // cancel in-flight if user clears/closes
          takeUntil(actions$.pipe(
            ofType(LeftoverActions.resetLeftoverSearchTerm, LeftoverActions.resetLeftoversBrief)
          )),
          map((results) => LeftoverActions.searchLeftoversBriefSuccess({ results })),
          catchError((error) => of(LeftoverActions.searchLeftoversBriefFailure({ error })))
        )
      )
    ),
  { functional: true }
);


export const LeftoverEffects = {
  loadLeftovers$,
  addLeftover$,
  updateLeftover$,
  deleteLeftover$,
  searchLeftoversBrief$
};
